import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import Maps from "../../components/Map";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import SearchAutocomplete from "../../components/SearchAutoComplete";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import clusterService from "../../services/cluster";
import facilityService from "../../services/facility";
import placeService from "../../services/place";
import service from "../../services/property";
import userService from "../../services/users";
import queryDefault from "../../utils/queryDefault";

export default function PropertyEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const [showCity, setShowCity] = useState(true);
  const [cities, setCities] = useState([]);
  const [showDistrict, setShowDistrict] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [showSubDistrict, setShowSubDistrict] = useState(true);
  const [subDistricts, setSubDistricts] = useState([]);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>({
    lat: -6.1753917,
    lng: 106.8271533,
  });
  const [loadingAddPhoto, setLoadingAddPhoto] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().min(2).required("Name is a required field and length > 2"),
    cluster_id: yup.number().moreThan(0).required("Cluster is a required field"),
    state_id: yup.number().moreThan(0).required("State is a required field"),
    city_id: yup.number().moreThan(0).required("City is a required field"),
    district_id: yup.number().moreThan(0).required("District is a required field"),
    sub_district_id: yup.number().moreThan(0).required("Sub District is a required field"),
    owner_id: yup.number().moreThan(0).required("Owner is a required field"),
  });
  const form = useForm({
    defaultValues: {
      name: "",
      is_favorite: false,
      cluster_id: 0,
      state_id: 0,
      city_id: 0,
      district_id: 0,
      sub_district_id: 0,
      owner_id: 0,
      type: "male",
      park_car: 0,
      park_motorcycle: 0,
      address: "",
      deposit: 0,
      location: { x: 0, y: 0 },
      admin_name: user.username,
      property_facilities: [],
      description: ""
    },
    resolver: yupResolver(schema),
  });

  const handleMarkerDragEnd = (lat: number, lng: number) => {
    setMarkerPosition({ lat, lng });
    form.setValue("location.x", lat);
    form.setValue("location.y", lng);
  };

  const handlePlaceSelected = (
    place: google.maps.places.PlaceResult | null
  ) => {
    if (place) {
      setMarkerPosition({
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      });
      form.setValue("location", {
        x: place.geometry?.location?.lat() ?? 0,
        y: place.geometry?.location?.lng() ?? 0,
      });
    }
  };

  const updateProperty = useMutation(
    "update_Property",
    service.updateProperty,
    {
      onSuccess: () => {
        navigate("/app/properties/property");

        notification.onOpen({
          message: "Property was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const listCluster = useQuery("list-cluster", () =>
    clusterService.getAllCluster({ keyword: "" })
  );

  const listState = useQuery("list-state", () =>
    placeService.getStates({})
  );

  const listFacility = useQuery("list-facility", () => facilityService.getAllFacility({ keyword: "", type: "Property" }));

  const listUser = useQuery("list-user", () =>
    userService.getUsers({ keyword: "", is_backoffice: true })
  );

  const onSubmit = (values: any) => {
    const submittedValues = { ...values, id };
    updateProperty.mutate(submittedValues);
  };

  const currentProperty = useQuery("current-property", () =>
    service.getPropertyById(Number(id))
  );

  useEffect(() => {
    if (currentProperty.isFetched) {
      form.setValue("name", get(currentProperty, "data.data.property.name"));
      form.setValue("is_favorite", get(currentProperty, "data.data.property.is_favorite"));
      form.setValue("cluster_id", get(currentProperty, "data.data.property.cluster_id"));
      form.setValue("owner_id", get(currentProperty, "data.data.property.owner_id"));
      form.setValue("type", get(currentProperty, "data.data.property.type"));
      form.setValue("park_car", get(currentProperty, "data.data.property.park_car"));
      form.setValue("park_motorcycle", get(currentProperty, "data.data.property.park_motorcycle"));
      form.setValue("address", get(currentProperty, "data.data.property.address"));
      form.setValue("deposit", get(currentProperty, "data.data.property.deposit"));
      form.setValue("location", {
        x: get(currentProperty, "data.data.property.location.x", 0),
        y: get(currentProperty, "data.data.property.location.y", 0),
      });
      form.setValue("sub_district_id", Number(get(currentProperty, "data.data.property.sub_district_id")));
      form.setValue("description", get(currentProperty, "data.data.property.description"));
      placeService.getSubDistrict(Number(get(currentProperty, "data.data.property.sub_district_id")))
        .then(response => {
          form.setValue("district_id", Number(response.data.district_id));
          placeService.getSubDistricts({ district_id: Number(response.data.district_id) })
            .then(response => {
              setSubDistricts(response.data);
            })
            .catch(error => { console.log(error) });
          placeService.getDistrict(Number(response.data.district_id))
            .then(response => {
              form.setValue("city_id", Number(response.data.city_id));
              placeService.getDistricts({ city_id: Number(response.data.city_id) })
                .then(response => {
                  setDistricts(response.data);
                })
                .catch(error => { console.log(error) });
              placeService.getCity(Number(response.data.city_id))
                .then(response => {
                  form.setValue("state_id", Number(response.data.state_id));
                  placeService.getCities({ state_id: Number(response.data.state_id) })
                    .then(response => {
                      setCities(response.data);
                    })
                    .catch(error => { console.log(error) });
                })
                .catch(error => { console.log(error) });
            })
            .catch(error => { console.log(error) });
        })
        .catch(error => { console.log(error) });
      setMarkerPosition({
        lat: get(currentProperty, "data.data.property.location.x"),
        lng: get(currentProperty, "data.data.property.location.y"),
      });
      form.setValue("property_facilities", get(currentProperty, "data.data.facilities").map((facility: any) => facility.facility_id));
      setSelectedElements(form.getValues("property_facilities"));
    }
    // eslint-disable-next-line
  }, [currentProperty.isFetched]);

  const [selectedElements, setSelectedElements] = useState<any>([]);

  const onSelectionChange = (event: any) => {
    const value = event.target.value;
    form.setValue("property_facilities", value);
    setSelectedElements(value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setLoadingAddPhoto(true);
      const formData = new FormData();
      formData.append("property", event.target.files[0]);
      formData.append("property_id", String(id));
      formData.append("admin_name", user.username);
      service.addPhoto(formData).then(() => {
        notification.onOpen({
          message: "Photo successfully added!",
          type: "success",
          position: "top",
        });
        currentProperty.refetch();
      }).catch(() => {
        notification.onOpen({
          message: "Failed add photo",
          type: "error",
          position: "top",
        });
      });
      setLoadingAddPhoto(false);
    }
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={`Edit Property ${get(currentProperty, "data.data.property.name")}`}
        breadcrumbs={[
          { label: "Properties", href: "/app/properties/property" },
          {
            label: "Edit Property",
            href: `/app/properties/property/edit/${id}`,
          },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="property"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Description</FormLabel>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="medium"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Cluster</FormLabel>
                <Controller
                  name="cluster_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={0} key={0}>Select Cluster</MenuItem>
                        {get(listCluster, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Is Favorite</FormLabel>
                <Controller
                  name="is_favorite"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Switch
                        {...field}
                        onChange={event => {
                          form.setValue("is_favorite", Boolean(event.target.value))
                        }}
                        value={form.getValues("is_favorite")}
                        checked={form.getValues("is_favorite")}
                      />
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>State</FormLabel>
                <Controller
                  name="state_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        onChange={event => {
                          form.setValue("state_id", Number(event.target.value));
                          placeService.getCities({ state_id: Number(event.target.value) }).then(response => {
                            setCities(response.data);
                            setShowCity(true);
                            form.setValue("city_id", 0);
                            setShowDistrict(false);
                            form.setValue("district_id", 0);
                            setShowSubDistrict(false);
                            form.setValue("sub_district_id", 0);
                          }).catch(error => {
                            console.log(error)
                          })
                        }}
                      >
                        <MenuItem value={0} key={0}>Select State</MenuItem>
                        {get(listState, ["data", "data"], [])?.map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              {showCity && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>City</FormLabel>
                  <Controller
                    name="city_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          onChange={event => {
                            form.setValue("city_id", Number(event.target.value));
                            placeService.getDistricts({ city_id: Number(event.target.value) }).then(response => {
                              setDistricts(response.data);
                              setShowDistrict(true);
                              form.setValue("district_id", 0);
                              setShowSubDistrict(false);
                              form.setValue("sub_district_id", 0);
                            }).catch(error => {
                              console.log(error)
                            })
                          }}
                        >
                          <MenuItem value={0} key={0}>Select City</MenuItem>
                          {cities?.map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
              )}

              {showDistrict && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>District</FormLabel>
                  <Controller
                    name="district_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          onChange={event => {
                            form.setValue("district_id", Number(event.target.value));
                            placeService.getSubDistricts({ district_id: Number(event.target.value) }).then(response => {
                              setSubDistricts(response.data);
                              setShowSubDistrict(true);
                              form.setValue("sub_district_id", 0);
                            }).catch(error => {
                              console.log(error)
                            })
                          }}
                        >
                          <MenuItem value={0} key={0}>Select District</MenuItem>
                          {districts?.map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
              )}

              {showSubDistrict && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Sub District</FormLabel>
                  <Controller
                    name="sub_district_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                        >
                          <MenuItem value={0} key={0}>Select Sub District</MenuItem>
                          {subDistricts?.map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
              )}

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Owner</FormLabel>
                <Controller
                  name="owner_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={0} key={0}>Select Owner</MenuItem>
                        {get(listUser, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={"male"} key={0}>Male Only</MenuItem>
                        <MenuItem value={"female"} key={1}>Female Only</MenuItem>
                        <MenuItem value={"mix"} key={2}>Mix</MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Facility</FormLabel>
                <Controller
                  name="property_facilities"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        multiple
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        renderValue={(selected: any) => selected.map((id: any) => {
                          var facility = ""
                          listFacility?.data?.data?.forEach((element: any) => {
                            if (element.id === id) {
                              facility = element.name
                            }
                          });
                          return facility;
                        }).join(", ")}
                        onChange={onSelectionChange}
                      >
                        {listFacility?.data?.data?.map((facility: any) => (
                          <MenuItem value={facility.id} key={facility.id}>
                            <ListItemText primary={facility.name} />
                            <Checkbox checked={selectedElements.indexOf(facility.id) > -1} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Car Park</FormLabel>
                <Controller
                  name="park_car"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Motorcycle Park</FormLabel>
                <Controller
                  name="park_motorcycle"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Address</FormLabel>
                <Controller
                  name="address"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Deposit</FormLabel>
                <Controller
                  name="deposit"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Location</FormLabel>
                <SearchAutocomplete onPlaceSelected={handlePlaceSelected} />
              </FormControl>
              <Box
                sx={{
                  width: "100%",
                  height: "300px",
                }}
              >
                <Maps
                  center={markerPosition}
                  onMarkerDragEnd={handleMarkerDragEnd}
                  dragable={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateProperty.isLoading}
              disabled={!currentProperty.isFetched || currentProperty.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>

        {currentProperty.isFetched && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3">Photos</Typography>
            {currentProperty?.data?.data?.photos.map((photo: any) => (
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid lg={10} md={10}>
                    <img
                      src={process.env.REACT_APP_BACKEND_URL + photo.photo}
                      alt="Property"
                      style={{
                        width: "100%",
                        borderRadius: "0.375rem",
                        objectFit: "cover",
                        alignSelf: "start"
                      }}
                    />
                  </Grid>
                  <Grid lg={2} md={2}>
                    <Button variant="contained" sx={{ width: "100%" }}
                      onClick={() => { service.deletePhoto(photo.id).then(() => currentProperty.refetch()) }}>
                      Delete
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        <Box mt={3}>
          <input
            type="file"
            name="property"
            onChange={(event) =>
              handleFileChange(event)
            }
            hidden
          />
          <LoadingButton
            variant="contained"
            loading={loadingAddPhoto}
            component="span"
            onClick={() => {
              let element: HTMLElement = document.querySelector(
                'input[name="property"]'
              )!;
              element.click();
            }}
          >
            Upload Photo
          </LoadingButton>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
